import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Table, TableWrap, TableHeader, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { cn } from '@/utils/cn';
import { FlagBadge } from '@/components/flags/FlagBadge';
import { parseAutomationFlags } from '@/utils/automationFlags';
import {
  daypartingRunStatus,
  formatBudgetPair,
  formatMultiplierWindowsSummary,
  formatOperatingWindows,
  hourlyBudgetProductsLabel,
  hourlyBudgetScopeLabel,
} from '@/utils/hourlyBudgetList';
import type { HourlyBudgetListItem } from '@/types/hourlyBudget';

export function DaypartingEmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-6 py-16 text-center">
      <p className="text-base font-medium text-foreground">No campaigns have an hourly budget</p>
      <p className="mt-1 text-sm text-foreground-muted">
        Add an hourly budget to control spend by time of day.
      </p>
      <Button className="mt-5" onClick={onAdd}>
        <Plus className="mr-1 h-4 w-4" />
        Add hourly budget
      </Button>
    </div>
  );
}

export function DaypartingStatus({ item }: { item: HourlyBudgetListItem }) {
  const status = daypartingRunStatus(item);
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span
        className={cn(
          'rounded-full border px-2 py-0.5 text-xs',
          status.tone === 'active' && 'border-success/20 bg-success-subtle text-success-ink',
          status.tone === 'inactive' && 'border-border bg-surface-muted text-foreground-muted',
          status.tone === 'blocked' && 'border-warning/20 bg-warning-subtle text-warning-ink'
        )}
      >
        {status.label}
      </span>
      {status.detail ? <span className="text-xs text-foreground-muted">{status.detail}</span> : null}
    </span>
  );
}

export function DaypartingTable({
  items,
  onEdit,
  onRemove,
}: {
  profileId: string;
  items: HourlyBudgetListItem[];
  onEdit: (item: HourlyBudgetListItem) => void;
  onRemove: (item: HourlyBudgetListItem) => void;
}) {
  return (
    <TableWrap>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Campaign</TableHead>
            <TableHead>Product</TableHead>
            <TableHead>Active</TableHead>
            <TableHead>Base budget / Hourly increment</TableHead>
            <TableHead>Operating windows</TableHead>
            <TableHead>Multiplier windows</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <tbody>
          {items.map((item) => {
            const name = hourlyBudgetScopeLabel(item);
            const product = hourlyBudgetProductsLabel(item);
            return (
              <TableRow key={item.id}>
                <TableCell className="font-medium">
                  <span className="inline-flex flex-wrap items-center gap-2">
                    {name}
                    <FlagBadge flags={parseAutomationFlags(item.flags)} />
                  </span>
                </TableCell>
                <TableCell>{product}</TableCell>
                <TableCell>
                  <DaypartingStatus item={item} />
                </TableCell>
                <TableCell className="text-sm">
                  {(() => {
                    const pair = formatBudgetPair(item);
                    return (
                      <>
                        <div>{pair.baseLine}</div>
                        <div className="text-xs text-foreground-muted">{pair.hourlyLine}</div>
                      </>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-sm text-foreground-muted">
                  {formatOperatingWindows(item.base_windows, item.schedule_mode)}
                </TableCell>
                <TableCell className="text-sm text-foreground-muted">
                  {formatMultiplierWindowsSummary(item)}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="rounded p-1 text-sm hover:bg-surface-muted"
                      onClick={() => onEdit(item)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="rounded p-1 text-sm text-error-ink hover:bg-surface-muted"
                      onClick={() => onRemove(item)}
                    >
                      Remove
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </Table>
    </TableWrap>
  );
}
